import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Save, CheckCircle2, Plus, Minus, Copy } from 'lucide-react';
import RouterOSVersionSelector from '../components/RouterOSVersionSelector';
import { generateHotspotScript } from '../lib/generators/hotspot';
import { saveToHistory } from '../lib/storage/database';

const HotspotWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatedScript, setGeneratedScript] = useState('');
  const [availableProfiles, setAvailableProfiles] = useState([]);
  
  const [formData, setFormData] = useState({
    con_fichas: false, // Ya no se pregunta, siempre false por defecto
    perfiles: [],
    wifi: {
      tiene_wifi: false,
      activar: false,
      ssid: 'Internet Por Fichas'
    },
    wan: {
      puerto: 'ether1',
      tipo: 'dhcp',
      ip: '',
      gateway: '',
      netmask: '255.255.255.0'
    },
    puertos_lan: 4,
    router: {
      nombre: '',
      zona_horaria: 'America/Mexico_City',
      subnet_lan: '192.168.1.0/24',
      dns_servers: ['8.8.8.8', '8.8.4.4']
    },
    system_config: {
      deshabilitar_www: false,
      configurar_ntp: false,
      ntp_server: '192.36.143.130',
      reinicio_programado: false,
      hora_reinicio: '05:00:00',
      crear_usuario_admin: false,
      admin_username: 'admin',
      admin_password: '12345',
      eliminar_admin_default: false,
      bloquear_bluetooth: false,
      limpieza_automatica: false,
      intervalo_limpieza: '1h',
      habilitar_qr: false,
      nota_personalizada: false,
      texto_nota: ''
    },
    routeros_version: 'v6'
  });

  const totalSteps = 7; // Reducido de 8 a 7 (eliminamos step de "con fichas")

  // Ya no necesitamos fetchProfiles porque es 100% offline
  // Los perfiles ahora se manejan localmente

  const addProfile = (profile) => {
    const exists = formData.perfiles.find(p => p.nombre === profile.nombre);
    if (!exists) {
      setFormData({
        ...formData,
        perfiles: [...formData.perfiles, { ...profile, cantidad: 10 }]
      });
    }
  };

  const removeProfile = (profileName) => {
    setFormData({
      ...formData,
      perfiles: formData.perfiles.filter(p => p.nombre !== profileName)
    });
  };

  const updateProfileQuantity = (profileName, cantidad) => {
    setFormData({
      ...formData,
      perfiles: formData.perfiles.map(p =>
        p.nombre === profileName ? { ...p, cantidad: Math.max(1, cantidad) } : p
      )
    });
  };

  const generateScript = async () => {
    setLoading(true);
    try {
      // Preparar configuración para el generador local
      const config = {
        conFichas: formData.con_fichas,
        perfiles: formData.perfiles,
        wifi: formData.wifi,
        wan: formData.wan,
        puertosLan: formData.puertos_lan,
        router: {
          nombre: formData.router.nombre,
          zonaHoraria: formData.router.zona_horaria,
          subnetLan: formData.router.subnet_lan,
          dnsServers: formData.router.dns_servers
        },
        systemConfig: formData.system_config,
        routerosVersion: formData.routeros_version,
        adminUsername: formData.admin_username,
        adminPassword: formData.admin_password
      };
      
      // Generar script localmente (sin backend)
      const script = generateHotspotScript(config);
      
      setGeneratedScript(script);
      
      // Guardar en historial local
      saveToHistory({
        script,
        type: 'hotspot',
        config,
        nombre: `Hotspot - ${formData.router.nombre}`
      });
      
      // Auto-copiar al portapapeles
      copyToClipboard(script);
      
      alert('✅ Script generado y copiado al portapapeles');
    } catch (error) {
      console.error('Error generating script:', error);
      alert('❌ Error al generar el script: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Script copiado al portapapeles');
    }).catch(err => {
      console.error('Error al copiar:', err);
      alert('Error al copiar el script');
    });
  };

  const downloadScript = () => {
    const blob = new Blob([generatedScript], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hotspot_${Date.now()}.rsc`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // Step 1: WAN
        if (formData.wan.tipo === 'fijo') {
          return formData.wan.puerto && formData.wan.ip && formData.wan.gateway;
        }
        return formData.wan.puerto;
      case 2:
        // Step 2: Puertos LAN
        return formData.puertos_lan >= 1;
      case 3:
        // Step 3: WiFi
        return true;
      case 4:
        // Step 4: Router
        return formData.router.nombre !== '';
      case 5:
        // Step 5: RouterOS version selector
        return true;
      case 6:
        // Step 6: Configuraciones del sistema (opcionales)
        return true;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/scripts')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          
          <h1 className="text-3xl font-bold text-white">
            Configuración Hotspot
          </h1>
          
          <div className="w-20"></div>
        </div>

        {/* Progress Bar */}
        {currentStep < 7 && (
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div
                  key={step}
                  className={`flex items-center ${step < 6 ? 'flex-1' : ''}`}
                >
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
                      ${currentStep >= step
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-700 text-gray-400'
                      }
                    `}
                  >
                    {step}
                  </div>
                  {step < 6 && (
                    <div
                      className={`
                        flex-1 h-1 mx-2
                        ${currentStep > step ? 'bg-purple-500' : 'bg-gray-700'}
                      `}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>WAN</span>
              <span>Puertos</span>
              <span>WiFi</span>
              <span>Router</span>
              <span>Versión</span>
              <span>Sistema</span>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-gray-800 rounded-lg p-8 shadow-xl">
          {/* Step 1: Configurar WAN */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 1: Configurar WAN
              </h2>
              
              <p className="text-gray-300 mb-6">
                Configura la conexión de internet (WAN) del router
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Puerto WAN
                  </label>
                  <select
                    value={formData.wan.puerto}
                    onChange={(e) => setFormData({
                      ...formData,
                      wan: { ...formData.wan, puerto: e.target.value }
                    })}
                    data-testid="wan-puerto"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={`ether${num}`}>
                        ether{num}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Tipo de WAN
                  </label>
                  <select
                    value={formData.wan.tipo}
                    onChange={(e) => setFormData({
                      ...formData,
                      wan: { ...formData.wan, tipo: e.target.value }
                    })}
                    data-testid="wan-tipo"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  >
                    <option value="dhcp">DHCP</option>
                    <option value="fijo">IP Fija</option>
                  </select>
                </div>

                {formData.wan.tipo === 'fijo' && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">
                        IP WAN
                      </label>
                      <input
                        type="text"
                        value={formData.wan.ip}
                        onChange={(e) => setFormData({
                          ...formData,
                          wan: { ...formData.wan, ip: e.target.value }
                        })}
                        data-testid="wan-ip"
                        placeholder="192.168.1.25"
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-300 mb-2">
                        Gateway
                      </label>
                      <input
                        type="text"
                        value={formData.wan.gateway}
                        onChange={(e) => setFormData({
                          ...formData,
                          wan: { ...formData.wan, gateway: e.target.value }
                        })}
                        data-testid="wan-gateway"
                        placeholder="192.168.1.254"
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Puertos LAN */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 2: ¿Cuántos puertos LAN?
              </h2>
              
              <p className="text-gray-300 mb-6">
                Selecciona cuántos puertos ethernet quieres configurar para la red local
              </p>

              <div className="grid grid-cols-4 gap-3">
                {[2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <button
                    key={num}
                    onClick={() => setFormData({ ...formData, puertos_lan: num })}
                    data-testid={`lan-ports-${num}`}
                    className={`
                      p-4 rounded-lg border-2 transition-all
                      ${formData.puertos_lan === num
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-gray-600 hover:border-gray-500'
                      }
                    `}
                  >
                    <div className="text-2xl font-bold text-white">{num}</div>
                    <div className="text-xs text-gray-300">puertos</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Configurar WiFi */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 3: ¿El router tiene WiFi?
              </h2>
              
              <p className="text-gray-300 mb-6">
                Indica si tu router MikroTik tiene interfaz inalámbrica
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setFormData({
                    ...formData,
                    wifi: { ...formData.wifi, tiene_wifi: true }
                  })}
                  data-testid="tiene-wifi-si"
                  className={`
                    p-6 rounded-lg border-2 transition-all
                    ${formData.wifi.tiene_wifi
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-gray-600 hover:border-gray-500'
                    }
                  `}
                >
                  <h3 className="text-xl font-bold text-white mb-2">Sí, tiene WiFi</h3>
                  <p className="text-sm text-gray-300">
                    Configurar interfaz inalámbrica wlan1
                  </p>
                </button>

                <button
                  onClick={() => setFormData({
                    ...formData,
                    wifi: { tiene_wifi: false, activar: false, ssid: 'Internet Por Fichas' }
                  })}
                  data-testid="tiene-wifi-no"
                  className={`
                    p-6 rounded-lg border-2 transition-all
                    ${!formData.wifi.tiene_wifi
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-gray-600 hover:border-gray-500'
                    }
                  `}
                >
                  <h3 className="text-xl font-bold text-white mb-2">No, sin WiFi</h3>
                  <p className="text-sm text-gray-300">
                    Solo puertos ethernet
                  </p>
                </button>
              </div>

              {formData.wifi.tiene_wifi && (
                <div className="space-y-4 bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Configuración WiFi
                  </h3>

                  <div>
                    <label className="block text-sm text-gray-300 mb-2">
                      ¿Activar WiFi?
                    </label>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setFormData({
                          ...formData,
                          wifi: { ...formData.wifi, activar: true }
                        })}
                        data-testid="activar-wifi-si"
                        className={`
                          flex-1 p-3 rounded-lg border-2 transition-all
                          ${formData.wifi.activar
                            ? 'border-green-500 bg-green-500/20 text-white'
                            : 'border-gray-600 text-gray-300'
                          }
                        `}
                      >
                        Activado
                      </button>
                      <button
                        onClick={() => setFormData({
                          ...formData,
                          wifi: { ...formData.wifi, activar: false }
                        })}
                        data-testid="activar-wifi-no"
                        className={`
                          flex-1 p-3 rounded-lg border-2 transition-all
                          ${!formData.wifi.activar
                            ? 'border-red-500 bg-red-500/20 text-white'
                            : 'border-gray-600 text-gray-300'
                          }
                        `}
                      >
                        Desactivado
                      </button>
                    </div>
                  </div>

                  {formData.wifi.activar && (
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">
                        Nombre de la red WiFi (SSID)
                      </label>
                      <input
                        type="text"
                        value={formData.wifi.ssid}
                        onChange={(e) => setFormData({
                          ...formData,
                          wifi: { ...formData.wifi, ssid: e.target.value }
                        })}
                        data-testid="wifi-ssid"
                        placeholder="Internet Por Fichas"
                        className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Datos del Router */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 4: Datos del Router
              </h2>
              
              <p className="text-gray-300 mb-6">
                Información general del router y red local
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Nombre del Router *
                  </label>
                  <input
                    type="text"
                    value={formData.router.nombre}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: { ...formData.router, nombre: e.target.value }
                    })}
                    data-testid="router-nombre"
                    placeholder="Mi Hotspot"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Zona Horaria
                  </label>
                  <select
                    value={formData.router.zona_horaria}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: { ...formData.router, zona_horaria: e.target.value }
                    })}
                    data-testid="router-timezone"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  >
                    <option value="America/Mexico_City">America/Mexico_City</option>
                    <option value="America/Bogota">America/Bogota</option>
                    <option value="America/Lima">America/Lima</option>
                    <option value="America/Santiago">America/Santiago</option>
                    <option value="America/Buenos_Aires">America/Buenos_Aires</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Subnet LAN
                  </label>
                  <input
                    type="text"
                    value={formData.router.subnet_lan}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: { ...formData.router, subnet_lan: e.target.value }
                    })}
                    data-testid="router-subnet"
                    placeholder="192.168.1.0/24"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    DNS Servers
                  </label>
                  <input
                    type="text"
                    value={formData.router.dns_servers.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: {
                        ...formData.router,
                        dns_servers: e.target.value.split(',').map(s => s.trim())
                      }
                    })}
                    data-testid="router-dns"
                    placeholder="8.8.8.8, 8.8.4.4"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Versión de RouterOS */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 5: Versión de RouterOS
              </h2>
              <p className="text-gray-300 mb-6">
                Selecciona la versión de RouterOS para generar el script con la sintaxis correcta
              </p>
              
              <RouterOSVersionSelector
                value={formData.routeros_version}
                onChange={(version) => setFormData({ ...formData, routeros_version: version })}
              />
            </div>
          )}

          {/* Step 6: Configuraciones del Sistema */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 6: Configuraciones Adicionales del Sistema
              </h2>
              <p className="text-gray-300 mb-6">
                Opcionales: Configuraciones avanzadas de seguridad y mantenimiento
              </p>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition">
                  <input 
                    type="checkbox" 
                    checked={formData.system_config.deshabilitar_www}
                    onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, deshabilitar_www: e.target.checked}})}
                    className="w-5 h-5 rounded mt-0.5" 
                  />
                  <div>
                    <span className="text-white font-medium">Deshabilitar servicio WWW</span>
                    <p className="text-sm text-gray-400">Desactiva el acceso web al router por seguridad</p>
                  </div>
                </label>

                <div className="p-3 bg-gray-700 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer mb-2">
                    <input 
                      type="checkbox" 
                      checked={formData.system_config.configurar_ntp}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, configurar_ntp: e.target.checked}})}
                      className="w-5 h-5 rounded" 
                    />
                    <span className="text-white font-medium">Configurar NTP (Sincronización de hora)</span>
                  </label>
                  {formData.system_config.configurar_ntp && (
                    <input 
                      type="text" 
                      value={formData.system_config.ntp_server}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, ntp_server: e.target.value}})}
                      className="ml-8 w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                      placeholder="192.36.143.130" 
                    />
                  )}
                </div>

                <div className="p-3 bg-gray-700 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer mb-2">
                    <input 
                      type="checkbox" 
                      checked={formData.system_config.reinicio_programado}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, reinicio_programado: e.target.checked}})}
                      className="w-5 h-5 rounded" 
                    />
                    <span className="text-white font-medium">Reinicio Programado Diario</span>
                  </label>
                  {formData.system_config.reinicio_programado && (
                    <input 
                      type="time" 
                      value={formData.system_config.hora_reinicio.substring(0, 5)}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, hora_reinicio: e.target.value + ':00'}})}
                      className="ml-8 px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white" 
                    />
                  )}
                </div>

                <div className="p-3 bg-gray-700 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer mb-2">
                    <input 
                      type="checkbox" 
                      checked={formData.system_config.crear_usuario_admin}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, crear_usuario_admin: e.target.checked}})}
                      className="w-5 h-5 rounded" 
                    />
                    <div>
                      <span className="text-white font-medium">Crear Usuario Administrador Personalizado</span>
                      <p className="text-sm text-gray-400">Crea un usuario admin con credenciales personalizadas</p>
                    </div>
                  </label>
                  {formData.system_config.crear_usuario_admin && (
                    <div className="ml-8 space-y-3 mt-3">
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">
                          Nombre de Usuario
                        </label>
                        <input 
                          type="text" 
                          value={formData.system_config.admin_username}
                          onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, admin_username: e.target.value}})}
                          className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                          placeholder="admin" 
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">
                          Contraseña
                        </label>
                        <input 
                          type="text" 
                          value={formData.system_config.admin_password}
                          onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, admin_password: e.target.value}})}
                          className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                          placeholder="12345" 
                        />
                      </div>
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition">
                  <input 
                    type="checkbox" 
                    checked={formData.system_config.eliminar_admin_default}
                    onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, eliminar_admin_default: e.target.checked}})}
                    className="w-5 h-5 rounded mt-0.5" 
                  />
                  <div>
                    <span className="text-white font-medium">Eliminar usuario 'admin' por defecto</span>
                    <p className="text-sm text-gray-400">Mejora la seguridad eliminando el usuario admin predeterminado</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition">
                  <input 
                    type="checkbox" 
                    checked={formData.system_config.bloquear_bluetooth}
                    onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, bloquear_bluetooth: e.target.checked}})}
                    className="w-5 h-5 rounded mt-0.5" 
                  />
                  <div>
                    <span className="text-white font-medium">Bloquear compartir por Bluetooth</span>
                    <p className="text-sm text-gray-400">Evita que los usuarios compartan la conexión mediante tethering Bluetooth</p>
                  </div>
                </label>

                <div className="p-3 bg-gray-700 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer mb-2">
                    <input 
                      type="checkbox" 
                      checked={formData.system_config.limpieza_automatica}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, limpieza_automatica: e.target.checked}})}
                      className="w-5 h-5 rounded" 
                    />
                    <div>
                      <span className="text-white font-medium">Limpieza Automática de Usuarios Expirados</span>
                      <p className="text-sm text-gray-400">Elimina automáticamente usuarios y schedulers expirados</p>
                    </div>
                  </label>
                  {formData.system_config.limpieza_automatica && (
                    <select
                      value={formData.system_config.intervalo_limpieza}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, intervalo_limpieza: e.target.value}})}
                      className="ml-8 w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                    >
                      <option value="1h">Cada 1 hora</option>
                      <option value="6h">Cada 6 horas</option>
                      <option value="12h">Cada 12 horas</option>
                      <option value="1d">Cada 24 horas</option>
                    </select>
                  )}
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition">
                  <input 
                    type="checkbox" 
                    checked={formData.system_config.habilitar_qr}
                    onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, habilitar_qr: e.target.checked}})}
                    className="w-5 h-5 rounded mt-0.5" 
                  />
                  <div>
                    <span className="text-white font-medium">Habilitar Lector QR</span>
                    <p className="text-sm text-gray-400">Agrega laksa19.github.io al walled-garden para usar el lector QR</p>
                  </div>
                </label>

                <div className="p-3 bg-gray-700 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer mb-2">
                    <input 
                      type="checkbox" 
                      checked={formData.system_config.nota_personalizada}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, nota_personalizada: e.target.checked}})}
                      className="w-5 h-5 rounded" 
                    />
                    <span className="text-white font-medium">Nota Personalizada del Sistema</span>
                  </label>
                  {formData.system_config.nota_personalizada && (
                    <textarea 
                      rows="4" 
                      value={formData.system_config.texto_nota}
                      onChange={(e) => setFormData({...formData, system_config: {...formData.system_config, texto_nota: e.target.value}})}
                      className="ml-8 w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                      placeholder="Mensaje personalizado que se mostrará en el terminal del router..." 
                    />
                  )}
                </div>
              </div>
              
              {/* Mostrar script generado si existe */}
              {generatedScript && (
                <div className="mt-8 space-y-4 border-t border-gray-600 pt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">Script Generado y Guardado</h3>
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  
                  <div className="bg-gray-900 p-4 rounded-lg overflow-x-auto max-h-96">
                    <pre className="text-sm text-green-400 font-mono whitespace-pre">
                      {generatedScript}
                    </pre>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => copyToClipboard(generatedScript)}
                      data-testid="copy-script-btn"
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      <Copy className="w-5 h-5" />
                      Copiar al Portapapeles
                    </button>
                  </div>

                  <button
                    onClick={() => navigate('/scripts')}
                    data-testid="new-config-btn"
                    className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Nueva Configuración
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 7 && (
          <div className="flex justify-between mt-6">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              data-testid="prev-step-btn"
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Anterior
            </button>

            <button
              onClick={currentStep === 6 ? generateScript : nextStep}
              disabled={!canProceed() || loading}
              data-testid="next-step-btn"
              className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors min-w-[200px]"
            >
              {loading ? 'Generando...' : currentStep === 6 ? 'Generar Script' : 'Siguiente'}
              {currentStep < 6 && <ArrowRight className="w-5 h-5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotspotWizard;
